/**
 * A-2 班次（admin.schedule 權限；PRD §A-2 / AC-A2-1..5）+ 明確清單批量排班。
 */

import { apiFetch, type Schemas } from './core';

/**
 * 班次摘要（CMS 列表 / 編輯用）；對應 BE ScheduleResponseDto。
 * - departure_at / departure_end_at / created_at / updated_at 以 ISO 8601 字串傳遞
 *   （BE 回的 Date 經 JSON 序列化後即字串）。
 */
export type ScheduleSummary = Schemas['ScheduleResponseDto'];

export interface ListSchedulesResponse {
  items: ScheduleSummary[];
  total: number;
  limit: number;
  offset: number;
}

export interface CreateScheduleDto {
  product_id: string;
  departure_at: string; // ISO 8601
  departure_end_at: string; // ISO 8601
  capacity: number;
  min_passengers?: number;
  weather_cancelled?: boolean;
  cancellation_reason?: string;
  notes?: string;
}

export interface UpdateScheduleDto {
  capacity?: number;
  weather_cancelled?: boolean;
  cancellation_reason?: string;
  notes?: string;
  min_passengers?: number;
}

export function listSchedules(
  productId: string,
  opts?: {
    limit?: number;
    offset?: number;
    /** ISO 8601；對應 BE departure_at >= departureAfter（期間下界，含） */
    departureAfter?: string;
    /** ISO 8601；對應 BE departure_at < departureBefore（期間上界，不含） */
    departureBefore?: string;
    /** true=僅停航 / false=僅營運 / undefined=全部 */
    weatherCancelled?: boolean;
  },
): Promise<{ items: ScheduleSummary[] }> {
  const params = new URLSearchParams({ product_id: productId });
  if (opts?.limit !== undefined) params.set('limit', String(opts.limit));
  if (opts?.offset !== undefined) params.set('offset', String(opts.offset));
  if (opts?.departureAfter !== undefined) params.set('departure_after', opts.departureAfter);
  if (opts?.departureBefore !== undefined) params.set('departure_before', opts.departureBefore);
  if (opts?.weatherCancelled !== undefined)
    params.set('weather_cancelled', String(opts.weatherCancelled));
  // BE 回 ListSchedulesResponse；FE 對外僅承諾 { items }，內部仍接 full response。
  return apiFetch<ListSchedulesResponse>(`/schedules?${params.toString()}`, {
    method: 'GET',
  }).then((r) => ({ items: r.items }));
}

export function getSchedule(id: string): Promise<ScheduleSummary> {
  return apiFetch<ScheduleSummary>(`/schedules/${encodeURIComponent(id)}`, { method: 'GET' });
}

export function createSchedule(dto: CreateScheduleDto): Promise<ScheduleSummary> {
  return apiFetch<ScheduleSummary>('/schedules', { method: 'POST', body: dto });
}

export function updateSchedule(id: string, dto: UpdateScheduleDto): Promise<ScheduleSummary> {
  return apiFetch<ScheduleSummary>(`/schedules/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: dto,
  });
}

// ---------- A-2 排班（明確清單批量；AC-A2-2 重設計）----------

/** 明確班次清單之單筆（前端「排班」頁算好 日期×時段 組合，departure_* 為 ISO 8601）。 */
export interface BulkScheduleItem {
  departure_at: string;
  departure_end_at: string;
  capacity: number;
  min_passengers?: number;
  notes?: string;
}

export interface CreateSchedulesBulkDto {
  product_id: string;
  items: BulkScheduleItem[];
}

export interface BulkCreateSchedulesResponse {
  created: ScheduleSummary[];
  skipped: Array<{ departure_at: string; reason: string }>;
  created_count: number;
  skipped_count: number;
}

export function createSchedulesBulk(
  dto: CreateSchedulesBulkDto,
): Promise<BulkCreateSchedulesResponse> {
  return apiFetch<BulkCreateSchedulesResponse>('/schedules/bulk', { method: 'POST', body: dto });
}
